import React, { useEffect, useRef, useState } from "react";
import { Input, Checkbox, message, Button, Modal } from "antd";
import { LockOutlined, MailOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./login.css";
import drEasyApi from "../../../Api/Customapi";
import { HiUserCircle } from "react-icons/hi2";

const OTP_LENGTH = 6;

function OtpInput({ value, onChange, onComplete, disabled }) {
  const inputsRef = useRef([]);
  const digits = value
    .split("")
    .concat(Array(OTP_LENGTH).fill(""))
    .slice(0, OTP_LENGTH);

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "");
    if (!val) return;
    const char = val[val.length - 1];
    const newDigits = [...digits];
    newDigits[idx] = char;
    const newValue = newDigits.join("");
    onChange(newValue);
    if (idx < OTP_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
    if (newValue.replace(/\s/g, "").length === OTP_LENGTH)
      onComplete?.(newValue);
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      const newDigits = [...digits];
      if (newDigits[idx]) {
        newDigits[idx] = "";
        onChange(newDigits.join(""));
      } else if (idx > 0) {
        newDigits[idx - 1] = "";
        onChange(newDigits.join(""));
        inputsRef.current[idx - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    onChange(pasted.padEnd(OTP_LENGTH, "").slice(0, OTP_LENGTH));
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIdx]?.focus();
    if (pasted.length === OTP_LENGTH) onComplete?.(pasted);
  };

  return (
    <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
      {digits.map((digit, idx) => (
        <input
          key={idx}
          ref={(el) => (inputsRef.current[idx] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          style={{
            width: 48,
            height: 52,
            textAlign: "center",
            fontSize: 22,
            fontWeight: 600,
            border: digit ? "2px solid teal" : "1.5px solid #d9d9d9",
            borderRadius: 10,
            outline: "none",
            background: disabled ? "#f5f5f5" : "#fff",
            color: "#1a1a1a",
            caretColor: "teal",
            transition: "border-color 0.2s",
            cursor: disabled ? "not-allowed" : "text",
          }}
        />
      ))}
    </div>
  );
}

// Forgot modal steps: 'email' | 'otp' | 'reset'
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [shake, setShake] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  // Forgot password modal
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState("email");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotResendLoading, setForgotResendLoading] = useState(false);

  const navigate = useNavigate();

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };

  const openForgotModal = () => {
    setForgotEmail(email);
    setForgotOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setForgotStep("email");
    setForgotOpen(true);
  };

  const closeForgotModal = () => setForgotOpen(false);

  // ─── Login ────────────────────────────────────────────────────────────────
  const handleLogin = async () => {
    if (!email || !password) {
      message.warning("Please enter both email and password.");
      return;
    }
    setLoading(true);
    try {
      const response = await drEasyApi.post(`/api/admin/User/AdminLogin`, {
        email,
        password,
      });
      const { token, role } = response.data;
      localStorage.setItem("role", role);

      if (role == 1 || role == 2 || role == 0 || role == 5) {
        message.error("Unauthorized access.");
        return;
      }

      if (rememberMe) {
        localStorage.setItem("docTkn", token);
      } else {
        sessionStorage.setItem("docTkn", token);
      }

      // message.success("Login successful!");
      if (role == 3) {
        const modal = Modal.success({
          icon: null,
          footer: null,
          closable: false,
          centered: true,
          width: 360,
          content: (
            <div className="flex flex-col items-center text-center py-4 gap-3">
              <HiUserCircle size={72} className="text-green-500" />
              <div>
                <h2 className="text-xl font-bold text-gray-800 m-0">
                  Welcome, Admin!
                </h2>
                <p className="text-gray-500 mt-1 mb-0">
                  You have successfully logged in.
                </p>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1 mt-2 overflow-hidden">
                <div
                  className="bg-green-500 h-1 rounded-full"
                  style={{ animation: "shrink 3s linear forwards" }}
                />
              </div>
            </div>
          ),
        });

        // Auto close after 3 seconds
        setTimeout(() => modal.destroy(), 1000);
      } else if (role == 4 ) {
        const modal = Modal.success({
          icon: null,
          footer: null,
          closable: false,
          centered: true,
          width: 360,
          content: (
            <div className="flex flex-col items-center text-center py-4 gap-3">
              <HiUserCircle size={72} className="text-green-500" />
              <div>
                <h2 className="text-xl font-bold text-gray-800 m-0">
                  Welcome, Secertary!
                </h2>
                <p className="text-gray-500 mt-1 mb-0">
                  You have successfully logged in.
                </p>
              </div>
           
            </div>
          ),
        });

        // Auto close after 3 seconds
        setTimeout(() => modal.destroy(), 1000);
      } else if (role == 6 ||role==7) {
        const modal = Modal.success({
          icon: null,
          footer: null,
          closable: false,
          centered: true,
          width: 360,
          content: (
            <div className="flex flex-col items-center text-center py-4 gap-3">
              <HiUserCircle size={72} className="text-green-500" />
              <div>
                <h2 className="text-xl font-bold text-gray-800 m-0">
                  Welcome, Employee!
                </h2>
                <p className="text-gray-500 mt-1 mb-0">
                  You have successfully logged in.
                </p>
              </div>
             
            </div>
          ),
        });

        // Auto close after 3 seconds
        setTimeout(() => modal.destroy(), 1000);
      }
      window.location.href= (role == 3 ? "/dashboard" :role==7?"/dashboard-analysis": "/scanQr");
    } catch (error) {
      triggerShake();
      if (
        error.response?.status === 400 &&
        error.response?.data?.errors[0]?.includes("OTP has been sent.")
      ) {
        message.info(
          "OTP has been sent to your email. Please verify to continue.",
        );
        setShowOtpInput(true);
      } else if (error.response?.status === 400) {
        message.error("Invalid email or password. Please try again.");
      } else if (error.response?.data?.errors) {
        message.error(error.response.data.errors[0]);
      } else {
        message.error(
          "Login failed. Please check your connection and try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Login OTP verify ─────────────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    setLoading(true);
    try {
      // await drEasyApi.post(`/api/admin/User/Verify`, { email, otpCode });
      message.success("Email verified successfully!");

      const response = await drEasyApi.post(`/api/admin/User/AdminLogin`, {
        email,
        password,
      });
      const { token, role } = response.data;

      if (role !== 2) {
        message.error("Unauthorized access.");
        return;
      }

      if (rememberMe) {
        localStorage.setItem("docTkn", token);
      } else {
        sessionStorage.setItem("docTkn", token);
      }

      message.success("Login successful!");
      navigate("/dashboard");
    } catch (error) {
      triggerShake();
      if (error.response?.status === 400 || error.response?.status === 401) {
        message.error("Invalid OTP code. Please try again.");
      } else if (error.response?.data?.errors) {
        message.error(error.response.data.errors[0]);
      } else {
        message.error("Verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    try {
      // await drEasyApi.post(`/api/admin/User/ResendOtp`, { email });
      setOtpCode("");
      message.success("OTP has been resent to your email.");
    } catch (error) {
      message.error(
        error.response?.data?.errors?.[0] || "Failed to resend OTP.",
      );
    } finally {
      setResendLoading(false);
    }
  };

  // ─── Forgot — step 1: send OTP ────────────────────────────────────────────
  const handleForgotSendOtp = async () => {
    if (!forgotEmail) {
      message.warning("Please enter your email.");
      return;
    }
    setForgotLoading(true);
    try {
      await drEasyApi.post(`/api/{slug}/User/AdminForgotPassword`, {
        email: forgotEmail,
      });
      message.success("OTP sent to your email.");
      setForgotStep("otp");
    } catch (error) {
      message.error(error.response?.data?.errors?.[0] || "Failed to send OTP.");
    } finally {
      setForgotLoading(false);
    }
  };

  // ─── Forgot — step 2: verify OTP ─────────────────────────────────────────
  const handleForgotVerifyOtp = async () => {
    // if (forgotOtp.replace(/\s/g, "").length < OTP_LENGTH) {
    //     message.warning("Please enter the complete OTP code.");
    //     return;
    // }
    setForgotLoading(true);
    try {
      await drEasyApi.post(`/api/{slug}/User/AdminResetPassword`, {
        email: forgotEmail,
        resetCode: forgotOtp,
        newPassword,
      });
      message.success("OTP verified.");
      closeForgotModal();
    } catch (error) {
      message.error(error.response?.data?.errors?.[0] || "Invalid OTP.");
    } finally {
      setForgotLoading(false);
    }
  };

  // ─── Forgot — step 3: reset password ─────────────────────────────────────
  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      message.warning("Please fill in both password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      message.error("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      message.error("Password must be at least 6 characters.");
      return;
    }
    setForgotLoading(true);
    try {
      await drEasyApi.post(`/api/{slug}/User/AdminResetPassword`, {
        email: forgotEmail,
        resetCode: forgotOtp,
        newPassword,
      });
      message.success("Password reset successfully! Please log in.");
      closeForgotModal();
    } catch (error) {
      message.error(
        error.response?.data?.errors?.[0] || "Failed to reset password.",
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotResendOtp = async () => {
    setForgotResendLoading(true);
    try {
      await drEasyApi.post(`/api/{slug}/User/AdminForgotPassword`, {
        email: forgotEmail,
      });
      setForgotOtp("");
      message.success("OTP resent.");
    } catch (error) {
      message.error(
        error.response?.data?.errors?.[0] || "Failed to resend OTP.",
      );
    } finally {
      setForgotResendLoading(false);
    }
  };

  const handleKeyDown = (e, action) => {
    if (e.key === "Enter" && !loading) action();
  };

  useEffect(() => {
    localStorage.removeItem("docTkn");
  }, []);

  // ─── Modal config per step ────────────────────────────────────────────────
  const modalConfig = {
    email: {
      title: "Forgot Password",
      okText: "Send OTP",
      onOk: handleForgotSendOtp,
    },
    otp: { title: "Verify OTP", okText: "Verify", onOk: handleForgotVerifyOtp },
    reset: {
      title: "Reset Password",
      okText: "Reset Password",
      onOk: handleResetPassword,
    },
  }[forgotStep];

  return (
    <div className="h-screen flex flex-col sm:flex-row bg-white sm:p-0 p-5">
      {/* Left Side */}
      <div className="w-full sm:w-1/2 h-2/5 bg-primary sm:h-full sm:rounded-none rounded-full aspect-square text-white sm:p-10 flex flex-col justify-center items-center sm:py-10">
      
      
        <img
          src="https://res.cloudinary.com/dk2uh7sho/image/upload/v1755773011/detailed-illustration-online-learning-landing-page_vpf7yi.png"
          alt="Medical Illustration"
          className="w-[90%] opacity-80 "
        />
      </div>

      {/* Right Side */}
      <div className="w-full sm:w-1/2 sm:p-10 mt-3 sm:mt-0 flex flex-col items-center justify-center sm:h-full h-3/5">
        <div className="mb-10 text-center">
          <h1 className="text-2xl font-bold text-black sm:hidden">
            Welcome Back
          </h1>
          <h2 className="text-2xl font-bold text-gray-800 sm:inline-block hidden">
            {showOtpInput ? "Email Verification" : "Centers Login"}
          </h2>
          <p className="text-gray-500 mt-2 sm:text-base text-sm">
            {showOtpInput
              ? "Enter the OTP code sent to your email to verify your account."
              : "Sign in to manage your practice, view analytics, and update records securely."}
          </p>
        </div>

        <div className={`space-y-4 w-full max-w-xl ${shake ? "shake" : ""}`}>
          {!showOtpInput ? (
            <>
              <Input
                size="large"
                placeholder="Enter your email"
                prefix={<MailOutlined />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleLogin)}
              />
              <Input.Password
                size="large"
                placeholder="Enter your password"
                prefix={<LockOutlined />}
                iconRender={() => <EyeOutlined />}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleLogin)}
              />
              <div className="flex justify-between items-center">
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                >
                  Remember me
                </Checkbox>
                <Button
                  type="link"
                  className="text-primary hover:text-primary_hover p-0"
                  onClick={openForgotModal}
                >
                  Forgot password?
                </Button>
              </div>
              <button
                onClick={handleLogin}
                disabled={loading}
                className={`w-full py-3 px-6 text-white text-lg font-semibold rounded-xl ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:bg-primary_hover"
                } transition duration-200 focus:outline-none focus:ring-2 focus:ring-primary_hover`}
              >
                {loading ? "Logging in..." : "Confirm Login"}
              </button>
            </>
          ) : (
            <>
              <Input
                size="large"
                prefix={<MailOutlined />}
                value={email}
                disabled
                className="bg-gray-50"
              />

              {/* Split OTP boxes */}
              <div className="py-2">
                <OtpInput
                  value={otpCode}
                  onChange={setOtpCode}
                  onComplete={handleVerifyOtp}
                  disabled={loading}
                />
              </div>

              <button
                onClick={handleVerifyOtp}
                disabled={loading}
                className={`w-full py-3 px-6 text-white text-lg font-semibold rounded-xl ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:bg-primary_hover"
                } transition duration-200`}
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </button>
              <div className="flex flex-col space-y-1">
                <Button
                  type="link"
                  onClick={handleResendOtp}
                  loading={resendLoading}
                  className="text-primary"
                >
                  Resend OTP
                </Button>
                <Button
                  type="link"
                  onClick={() => {
                    setShowOtpInput(false);
                    setOtpCode("");
                  }}
                  className="text-gray-500"
                >
                  Back to Login
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Forgot Password Modal ─────────────────────────────────────── */}
      <Modal
        title={modalConfig.title}
        open={forgotOpen}
        onCancel={closeForgotModal}
        onOk={modalConfig.onOk}
        okText={modalConfig.okText}
        confirmLoading={forgotLoading}
        okButtonProps={{
          style: { backgroundColor: "teal", borderColor: "teal" },
        }}
        destroyOnClose
        width={420}
      >
        <div className="space-y-4 py-2">
          {/* Step 1 — enter email */}
          {forgotStep === "email" && (
            <>
              <p className="text-gray-500 text-sm">
                Enter your registered email and we'll send you an OTP to reset
                your password.
              </p>
              <Input
                size="large"
                placeholder="Enter your email"
                prefix={<MailOutlined />}
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleForgotSendOtp();
                }}
              />
            </>
          )}

          {/* Step 2 — split OTP boxes */}
          {forgotStep === "otp" && (
            <>
              <p className="text-gray-500 text-sm">
                An OTP was sent to{" "}
                <span className="font-medium text-gray-700">{forgotEmail}</span>
                . Enter it below.
              </p>
              <div className="py-2">
                <OtpInput
                  value={forgotOtp}
                  onChange={setForgotOtp}
                  //   onComplete={handleForgotVerifyOtp}
                  disabled={forgotLoading}
                />
              </div>
              <>
                <p className="text-gray-500 text-sm">
                  Enter and confirm your new password.
                </p>
                <Input.Password
                  size="large"
                  placeholder="New password"
                  prefix={<LockOutlined />}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <Input.Password
                  size="large"
                  placeholder="Confirm new password"
                  prefix={<LockOutlined />}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleResetPassword();
                  }}
                />
              </>
              <div className="flex gap-3">
                <Button
                  type="link"
                  size="small"
                  loading={forgotResendLoading}
                  onClick={handleForgotResendOtp}
                  className="text-primary p-0"
                >
                  Resend OTP
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
